import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toDateInput, formatDOB } from "@/lib/format";
import { toast } from "sonner";
import {
  DoorOpen, User, CalendarCheck, CalendarX, Hash, MapPin, MapPinned, Layers,
  UserCog, MessageSquare, PlusCircle, Search, Lightbulb, Loader2, Send, AlertTriangle, Info, CheckCircle2, Cake, MessageCircle,
} from "lucide-react";

const priorities = ["Low", "Normal", "High", "Urgent"];

export default function CreateGlitch() {
  const { user, settings } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchRoom, setSearchRoom] = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const emptyForm = {
    room_no: "", guest_name: "", checkin: "", checkout: "", ref_no: "",
    location: "", sub_location: "", forbes_class: "", priority: "Normal",
    created_by: user?.username || "", description: "",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleGuest = (name) => {
    setSelectedGuests((prev) => {
      const next = prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name];
      setForm((f) => ({ ...f, guest_name: next.join(", ") }));
      return next;
    });
  };

  const onLocation = async (v) => {
    setForm((f) => ({ ...f, location: v, sub_location: "", forbes_class: "" }));
    setSubs([]); setClasses([]);
    if (v) {
      const { data } = await api.get("/classifications/sublocations", { params: { department: v } });
      setSubs(data);
    }
  };

  const onSub = async (v) => {
    setForm((f) => ({ ...f, sub_location: v, forbes_class: "" }));
    setClasses([]);
    if (v) {
      const { data } = await api.get("/classifications/forbes", {
        params: { department: form.location, sub_location: v },
      });
      setClasses(data);
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!searchRoom) return;
    setSearching(true);
    try {
      const { data } = await api.get("/opera/search", { params: { room: searchRoom } });
      if (data.found) {
        setFound(data);
        const names = (data.guest_list || []).map((g) => g.name);
        setSelectedGuests(names);
        setForm((f) => ({
          ...f, room_no: data.room, guest_name: names.join(", "),
          checkin: toDateInput(data.checkin), checkout: toDateInput(data.checkout), ref_no: data.ref_no || "",
        }));
      } else {
        setFound(false);
        toast.warning("No guest found for that room");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.location) {
      toast.error("Location and description are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/glitches", form);
      toast.success("Glitch created & notifications dispatched!");
      setForm({ ...emptyForm, created_by: user?.username || "" });
      setSubs([]); setClasses([]); setFound(null); setSearchRoom("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field =
    "w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition text-sm bg-white";
  const lbl = "text-[13px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5";
  const waLink = settings?.general?.whatsapp_group_link;
  const tgLink = settings?.general?.telegram_group_link;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* OPERA SEARCH — on top */}
      <div className="bg-white rounded-2xl gm-shadow overflow-hidden gm-fade-up">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 text-[#4e73df]">
          <Search className="w-5 h-5" />
          <h3 className="font-bold text-[#3a3b45]">Search From Opera</h3>
        </div>
        <div className="p-6">
          <form onSubmit={doSearch} className="flex flex-col sm:flex-row gap-2">
            <input className={field} placeholder="Enter Room Number" value={searchRoom} data-testid="opera-search-input" onChange={(e) => setSearchRoom(e.target.value)} />
            <button data-testid="opera-search-btn" className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold shrink-0">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
            </button>
          </form>
          {found && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" /> Guest information found and populated
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InfoRow icon={DoorOpen} label="Room" value={found.room} />
                <InfoRow icon={Hash} label="Reference" value={found.ref_no || "-"} />
                <InfoRow icon={Layers} label="RM Type" value={found.rm_type || "-"} />
                <InfoRow icon={MapPin} label="Nationality" value={found.nationality || "-"} />
                <InfoRow icon={CalendarCheck} label="Check-In" value={found.checkin || "-"} />
                <InfoRow icon={CalendarX} label="Check-Out" value={found.checkout || "-"} />
                <InfoRow icon={CalendarCheck} label="Nights" value={found.nights || "-"} />
                <InfoRow icon={User} label="Adults" value={found.adults || "-"} />
                <InfoRow icon={MessageSquare} label="Meal Plan" value={found.meal_plan || "-"} />
              </div>
              {found.guest_list && found.guest_list.length > 0 && (
                <div>
                  <p className="text-[13px] font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Guests in this room — select who to include
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="opera-guest-list">
                    {found.guest_list.map((g, i) => {
                      const active = selectedGuests.includes(g.name);
                      return (
                        <button type="button" key={i} data-testid={`guest-chip-${i}`} onClick={() => toggleGuest(g.name)}
                          className={`w-full sm:w-auto flex flex-col items-start gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition ${active ? "bg-[#4e73df] text-white border-[#4e73df]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4e73df]"}`}>
                          <span className="flex items-center gap-2 min-w-0">
                            {active ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <User className="w-4 h-4 shrink-0" />}
                            <span className="break-words text-left">{g.name}</span>
                            {g.adults && g.adults !== "0" ? <span className={`shrink-0 text-[10px] ${active ? "text-white/80" : "text-slate-400"}`}>({g.adults} ad)</span> : null}
                          </span>
                          {g.dob ? <span className={`flex items-center gap-1 text-[10px] pl-6 ${active ? "text-white/80" : "text-slate-400"}`}><Cake className="w-3 h-3 shrink-0" /> {formatDOB(g.dob)}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {found === false && (
            <p className="mt-3 text-sm text-amber-600 flex items-center gap-1.5"><Info className="w-4 h-4" /> No guest found — you can still enter details manually below.</p>
          )}
        </div>
      </div>

      {/* GLITCH FORM */}
      <div className="bg-white rounded-2xl gm-shadow overflow-hidden gm-fade-up">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 text-[#4e73df]">
          <PlusCircle className="w-5 h-5" />
          <h2 className="font-bold text-lg text-[#3a3b45]">Glitch Entry Form</h2>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="glitch-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="sm:col-span-1">
              <label className={lbl}><DoorOpen className="w-3.5 h-3.5" /> Room #</label>
              <input className={field} value={form.room_no} data-testid="glitch-room" onChange={(e) => set("room_no", e.target.value)} />
            </div>
            <div className="sm:col-span-3">
              <label className={lbl}><User className="w-3.5 h-3.5" /> Guest Name</label>
              <input className={field} value={form.guest_name} data-testid="glitch-guest" onChange={(e) => set("guest_name", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={lbl}><CalendarCheck className="w-3.5 h-3.5" /> Check-In</label>
              <input type="date" className={field} value={form.checkin} data-testid="glitch-checkin" onChange={(e) => set("checkin", e.target.value)} />
            </div>
            <div>
              <label className={lbl}><CalendarX className="w-3.5 h-3.5" /> Check-Out</label>
              <input type="date" className={field} value={form.checkout} data-testid="glitch-checkout" onChange={(e) => set("checkout", e.target.value)} />
            </div>
            <div>
              <label className={lbl}><Hash className="w-3.5 h-3.5" /> Reference No</label>
              <input className={field} value={form.ref_no} data-testid="glitch-refno" onChange={(e) => set("ref_no", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}><MapPin className="w-3.5 h-3.5" /> Location</label>
              <select className={field} value={form.location} data-testid="glitch-location" onChange={(e) => onLocation(e.target.value)} required>
                <option value="">Select Location</option>
                {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}><MapPinned className="w-3.5 h-3.5" /> Sub Location</label>
              <select className={field} value={form.sub_location} data-testid="glitch-sublocation" onChange={(e) => onSub(e.target.value)} disabled={!subs.length}>
                <option value="">Select Sub Location</option>
                {subs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className={lbl}><Layers className="w-3.5 h-3.5" /> Forbes Classification</label>
              <select className={field} value={form.forbes_class} data-testid="glitch-forbes" onChange={(e) => set("forbes_class", e.target.value)}>
                <option value="">Select Classification</option>
                {classes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}><AlertTriangle className="w-3.5 h-3.5" /> Priority</label>
              <select className={field} value={form.priority} data-testid="glitch-priority" onChange={(e) => set("priority", e.target.value)}>
                {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}><UserCog className="w-3.5 h-3.5" /> Created By</label>
              <input className={field} value={form.created_by} data-testid="glitch-createdby" onChange={(e) => set("created_by", e.target.value)} required />
            </div>
          </div>

          <div>
            <label className={lbl}><MessageSquare className="w-3.5 h-3.5" /> Incident Description</label>
            <textarea className={field} rows={4} value={form.description} data-testid="glitch-description" onChange={(e) => set("description", e.target.value)} placeholder="Describe the incident..." required />
          </div>

          <div className="flex justify-end">
            <button data-testid="glitch-submit" disabled={saving} className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold px-6 py-2.5 rounded-lg transition-all hover:-translate-y-0.5 gm-shadow flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />} Create Glitch
            </button>
          </div>
        </form>
      </div>

      {/* ALERT GROUPS + QUICK TIPS — footer, all the way down */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(waLink || tgLink) && (
          <div className="bg-white rounded-2xl gm-shadow overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 text-[#4e73df]">
              <Send className="w-5 h-5" /><h3 className="font-bold text-[#3a3b45]">Alert Groups</h3>
            </div>
            <div className="p-5 space-y-2">
              {tgLink && <a href={tgLink} target="_blank" rel="noreferrer" data-testid="telegram-join" className="flex items-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg bg-[#229ED9]/10 text-[#1c86bd] hover:bg-[#229ED9]/20 transition"><Send className="w-4 h-4" /> Join Telegram Group</a>}
              {waLink && <a href={waLink} target="_blank" rel="noreferrer" data-testid="whatsapp-join" className="flex items-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg bg-[#25D366]/10 text-[#1da851] hover:bg-[#25D366]/20 transition"><MessageSquare className="w-4 h-4" /> Join WhatsApp Group</a>}
            </div>
          </div>
        )}

        <div className={`bg-white rounded-2xl gm-shadow overflow-hidden ${(waLink || tgLink) ? "" : "md:col-span-2"}`}>
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 text-[#4e73df]">
            <Lightbulb className="w-5 h-5" /><h3 className="font-bold text-[#3a3b45]">Quick Tips</h3>
          </div>
          <ul className={`p-5 gap-3 text-sm text-slate-600 grid grid-cols-1 ${(waLink || tgLink) ? "" : "md:grid-cols-2"}`}>
            <li className="flex gap-2.5"><Search className="w-4 h-4 text-[#4e73df] mt-0.5 shrink-0" /> Search by room number to auto-fill guest information and date of birth.</li>
            <li className="flex gap-2.5"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Set the correct priority so urgent items escalate faster.</li>
            <li className="flex gap-2.5"><MessageSquare className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> Be detailed in descriptions for faster resolution.</li>
            <li className="flex gap-2.5"><MessageCircle className="w-4 h-4 text-[#4e73df] mt-0.5 shrink-0" /> Use Guest Log to keep a running conversation history for a room, separate from glitches.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, full }) {
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg bg-slate-50 ${full ? "sm:col-span-2" : ""}`}>
      <Icon className="w-4 h-4 text-[#4e73df] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 leading-none">{label}</p>
        <p className="text-sm font-medium text-slate-700 break-words">{value}</p>
      </div>
    </div>
  );
}
